import { useState, useCallback, useMemo } from 'react';
import { TimelineEvent, ReleaseStepType } from '../../types';
import {
    useGetTimelineEventsHistoryQuery,
    useGetTimelineEventsQuery,
    useLazyGetTimelineEventsHistoryQuery,
    useLazyGetTimelineEventsQuery,
} from '../../middlewares/paginationApi';
import dayjs from 'dayjs';
import { PaginationInfo } from '@/actions/pagination';

interface UseTimelineEventsPaginationProps {
    orgId: string;
    appId: string;
    releaseId?: string;
    releaseStepType?: ReleaseStepType;
    workflowId?: string;
    workflowRunId?: string;
    stepRunIds?: string[];
    initialLoad?: boolean;
}

interface UseTimelineEventsPaginationReturn {
    events: TimelineEvent[];
    paginationInfo: PaginationInfo;
    isLoading: boolean;
    isLoadingMore: boolean;
    error: boolean;
    errorMessage?: string;
    loadMore: () => void;
    refresh: () => void;
    reset: () => void;
}

export const useTimelineEventsPagination = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    workflowId,
    workflowRunId,
    stepRunIds,
    initialLoad = true,
}: UseTimelineEventsPaginationProps): UseTimelineEventsPaginationReturn => {
    const [allEvents, setAllEvents] = useState<TimelineEvent[]>([]);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>(null);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const isWorkflowRequest = workflowId?.length && stepRunIds?.length;
    const [success, setSuccess] = useState<boolean>(true);

    const baseRequest = useMemo(
        () => ({
            orgId,
            appId,
            releaseId,
            releaseStepType,
            workflowId,
            workflowRunId,
            stepRunIds,
        }),
        [orgId, appId, releaseId, releaseStepType, workflowId, workflowRunId, stepRunIds],
    );

    const {
        data: initialData,
        isLoading: isInitialLoading,
        refetch: refetchInitial,
    } =
        isWorkflowRequest ?
            useGetTimelineEventsQuery(baseRequest, {
                skip: !initialLoad,
            })
        :   useGetTimelineEventsHistoryQuery(baseRequest, {
                skip: !initialLoad,
            });

    const [fetchMore, { data: moreData, isLoading: isLazyLoading }] =
        isWorkflowRequest ? useLazyGetTimelineEventsQuery() : useLazyGetTimelineEventsHistoryQuery();

    useMemo(() => {
        if (initialData?.data) {
            setAllEvents(initialData.data.events);
            setPaginationInfo(initialData.data.paginationInfo);
        }

        setSuccess(!initialData?.errorMessage?.length);
    }, [initialData]);

    useMemo(() => {
        if (moreData?.data) {
            const newEvents = moreData.data.events;
            const updatedEvents = sortCombinedEvents(allEvents, newEvents);

            setAllEvents(updatedEvents);
            setPaginationInfo(moreData.data.paginationInfo);
            setIsLoadingMore(false);
        }

        setSuccess(!moreData?.errorMessage?.length);
    }, [moreData]);

    const loadMore = useCallback(() => {
        if (!paginationInfo || isLoadingMore) return;

        setIsLoadingMore(true);

        fetchMore({
            ...baseRequest,
            nextCursor: paginationInfo.nextCursor,
        });
    }, [paginationInfo, isLoadingMore, fetchMore, baseRequest]);

    const refresh = useCallback(() => {
        setIsLoadingMore(false);
        setSuccess(true);
        refetchInitial();
    }, [refetchInitial]);

    const reset = useCallback(() => {
        setAllEvents([]);
        setSuccess(true);
        setIsLoadingMore(false);
    }, []);

    return {
        events: allEvents ?? [],
        paginationInfo: paginationInfo,
        isLoading: isInitialLoading,
        isLoadingMore: isLoadingMore || isLazyLoading,
        error: !success,
        loadMore,
        refresh,
        reset,
    };
};

const sortCombinedEvents = (allEvents: TimelineEvent[], newEvents: TimelineEvent[]) => {
    const updatedEvents = [...allEvents, ...newEvents];

    if (newEvents.length > 0) {
        const newestDate = dayjs(newEvents[0].triggeredAt);
        const oldestDate = dayjs(allEvents[allEvents.length - 1].triggeredAt);

        if (newestDate.isAfter(oldestDate)) {
            updatedEvents.sort((a, b) => dayjs(b.triggeredAt).diff(a.triggeredAt));
        }
    }

    return updatedEvents;
};
